import { signal } from '@preact/signals'
import ru from '../../../i18n/ru.json'
import { DEFAULT_LANG, LANG_KEY, detectLang, isLang, localeOf, type Lang } from './langs'

export { LANGS, LANG_NAMES, LANG_KEY, isLang, langFromPath, sitePath, type Lang } from './langs'

/**
 * Тексты интерфейса на четырёх языках (план `docs/i18n-plan.md`, раздел 6).
 *
 * Эталон — `i18n/ru.json`, он в сборке всегда; остальные словари подгружаются по требованию.
 * Ключи у всех одинаковые, это проверяет тест. Нет строки в текущем языке — берётся
 * английская, нет и её — русская: экран никогда не показывает голый ключ.
 *
 * Текущий язык и словарь — сигналы: `t()` читает их, и экраны перерисовываются при смене
 * языка без перезагрузки.
 */

type Dictionary = Record<string, string | string[]>
export type Key = keyof typeof ru

const ЭТАЛОН = ru as Dictionary
const загружено = new Map<Lang, Dictionary>([['ru', ЭТАЛОН]])
const файлы = import.meta.glob<{ default: Dictionary }>('../../../i18n/*.json')

function начальный(): Lang {
  try {
    return detectLang({
      stored: globalThis.localStorage?.getItem(LANG_KEY) ?? null,
      path: globalThis.location?.pathname,
      tags: globalThis.navigator?.languages ?? [],
    })
  } catch {
    return DEFAULT_LANG
  }
}

/** Текущий язык. До `initLang()` — определённый по хранилищу, адресу и браузеру. */
export const lang = signal<Lang>(начальный())
const словарь = signal<Dictionary>(ЭТАЛОН)

/**
 * Псевдолокаль: каждая строка удлинена на треть и взята в скобки — так переполнения
 * видны до того, как есть перевод (план, 10.4). Включается `?lang=xx` в сборке с холстом.
 */
export const pseudo = signal(false)

export async function loadDictionary(next: Lang): Promise<Dictionary> {
  const known = загружено.get(next)
  if (known !== undefined) return known
  const loader = файлы[`../../../i18n/${next}.json`]
  // Словаря ещё нет: экран остаётся русским, язык при этом считается выбранным
  if (loader === undefined) return ЭТАЛОН
  const module = await loader()
  загружено.set(next, module.default)
  return module.default
}

type LangLoader = (lang: Lang) => Promise<void>
const загрузчики: LangLoader[] = []

/**
 * Другие данные языка, которые должны быть под рукой к моменту переключения: тексты
 * программ таймера (`core/programs.ts`). Регистрация, а не импорт оттуда: иначе словарь
 * и программы импортировали бы друг друга по кругу. `setLang` дожидается всех
 * загрузчиков и только потом меняет сигнал — экраны не видят язык без его данных.
 */
export function onLangLoad(fn: LangLoader): void {
  загрузчики.push(fn)
}

function вДокумент(next: Lang): void {
  if (typeof document === 'undefined') return
  document.documentElement.lang = next
}

/** Сменить язык: словарь, атрибут документа и, если просили, запомнить выбор человека. */
export async function setLang(next: Lang, persist = true): Promise<void> {
  // Данные загрузчиков запасные (у текстов программ есть русский), поэтому их сбой язык не держит
  const [dict] = await Promise.all([loadDictionary(next), ...загрузчики.map((fn) => fn(next).catch(() => undefined))])
  // Английский запасной для всех: держим его под рукой, как только он понадобился
  if (next !== 'en' && next !== 'ru') void loadDictionary('en')
  lang.value = next
  словарь.value = dict
  вДокумент(next)
  if (!persist) return
  try {
    globalThis.localStorage?.setItem(LANG_KEY, next)
  } catch {
    // Приватный режим: выбор доживёт до закрытия вкладки
  }
}

/**
 * При старте: словарь определённого языка. `?lang=` в адресе — для прогонов и холста,
 * выбор по нему не запоминается.
 */
export async function initLang(): Promise<void> {
  const param = typeof location === 'undefined' ? null : new URLSearchParams(location.search).get('lang')
  if (__CANVAS__ && param === 'xx') pseudo.value = true
  if (isLang(param)) {
    await setLang(param, false)
    return
  }
  await setLang(lang.value, false)
}

function псевдо(text: string): string {
  if (!/\p{L}/u.test(text)) return text
  // Хвост с пробелом каждые восемь знаков: иначе длинная строка не переносится вовсе,
  // а настоящие языки так себя не ведут — даже китайский рвётся между иероглифами
  const хвост = '~'.repeat(Math.ceil(text.length * 0.35)).replace(/(~{8})(?=~)/g, '$1 ')
  return `[${text}${хвост}]`
}

/** `t('timer_subtitle', '2–3 месяца', 75, 90)` → «2–3 месяца · окно 75–90 мин». */
export function t(key: Key, ...args: Array<string | number>): string {
  const raw = словарь.value[key] ?? загружено.get('en')?.[key] ?? ЭТАЛОН[key]
  if (typeof raw !== 'string') return String(key)
  const text = raw.replace(/\{(\d)\}/g, (_, index: string) => String(args[Number(index)] ?? ''))
  return pseudo.value ? псевдо(text) : text
}

/** Массивы строк: короткие названия дней недели. */
export function tList(key: Key): string[] {
  const value = словарь.value[key] ?? загружено.get('en')?.[key] ?? ЭТАЛОН[key]
  return Array.isArray(value) ? value : []
}

/** Основы ключей со множественным числом: у `windows_one` основа `windows`. */
export type PluralBase = { [K in Key]: K extends `${infer B}_one` ? B : never }[Key]

const правила = new Map<Lang, Intl.PluralRules>()

/**
 * Множественное число: `plural(n, 'windows')` → «5 окон бодрствования».
 * Категория — у `Intl.PluralRules` текущего языка (ru: one/few/many, en и es: one/other,
 * zh: other), ключ — `<основа>_<категория>`; нет такой — `_other`, затем `_many`.
 */
export function plural(n: number, base: PluralBase): string {
  let rules = правила.get(lang.value)
  if (rules === undefined) {
    rules = new Intl.PluralRules(localeOf(lang.value))
    правила.set(lang.value, rules)
  }
  const category = rules.select(n)
  const есть = (k: string) => typeof (словарь.value[k] ?? ЭТАЛОН[k]) === 'string'
  const key = [`${base}_${category}`, `${base}_other`, `${base}_many`].find(есть)
  return key === undefined ? String(n) : t(key as Key, n)
}

// ---- даты: форматтеры создаются от текущего языка и кэшируются по нему ----

const форматтеры = new Map<string, Intl.DateTimeFormat>()

function формат(name: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${lang.value}:${name}`
  let f = форматтеры.get(key)
  if (f === undefined) {
    f = new Intl.DateTimeFormat(localeOf(lang.value), options)
    форматтеры.set(key, f)
  }
  return f
}

const заглавная = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

/** «14:05» — 24 часа во всех языках (решение 11). */
export function formatTime(millis: number): string {
  return формат('time', { hour: 'numeric', minute: '2-digit', hourCycle: 'h23' }).format(new Date(millis))
}

/** «Сентябрь» */
export function formatMonth(millis: number): string {
  return заглавная(формат('month', { month: 'long' }).format(new Date(millis)))
}

/** «Сентябрь» по номеру месяца (0–11) — для выпадающего списка в выборе даты. */
export function monthName(index: number): string {
  return заглавная(формат('month', { month: 'long' }).format(new Date(2026, index, 1)))
}

/** «18 сентября» */
export function formatDayMonth(millis: number): string {
  return формат('dayMonth', { day: 'numeric', month: 'long' }).format(new Date(millis))
}

/** «18 сент» — для осей графиков, где «18 сентября» не помещается. */
export function formatDayMonthShort(millis: number): string {
  return формат('dayMonthShort', { day: 'numeric', month: 'short' }).format(new Date(millis)).replace(/\.$/, '')
}

/** «Пятница, 18 сентября» */
export function formatWeekdayDayMonth(millis: number): string {
  return заглавная(формат('weekdayDayMonth', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(millis)))
}
